import { useEffect, useMemo, useState } from 'react';
import type { Client, LfCompetitor, LfFullScan, LfScan } from '../types';
import { getLfScan } from '../lib/api';
import { useIsMobile } from '../lib/useIsMobile';
import {
  GBIBadge, GBILabel, T, sharedStyles,
} from './Shared';

type SortKey = 'solv' | 'arp' | 'atrp' | 'reviews' | 'rating';

interface CompetitorPanelProps {
  client: Client;
  lfScans: LfScan[];
}

export function CompetitorPanel({ client, lfScans }: CompetitorPanelProps) {
  const hasScans = lfScans.length > 0;
  const isMobile = useIsMobile();

  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(
    hasScans ? lfScans[0].keyword : null
  );
  const [fullScan, setFullScan] = useState<LfFullScan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('solv');

  // Snap selectedKeyword back to the first available pill if the list shifts.
  useEffect(() => {
    if (!hasScans) { setSelectedKeyword(null); return; }
    if (!selectedKeyword || !lfScans.find(s => s.keyword === selectedKeyword)) {
      setSelectedKeyword(lfScans[0].keyword);
    }
  }, [lfScans, hasScans, selectedKeyword]);

  // Fetch the full scan whenever keyword changes.
  useEffect(() => {
    if (!selectedKeyword) { setFullScan(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getLfScan(client.id, selectedKeyword)
      .then(scan => { if (!cancelled) setFullScan(scan); })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [client.id, selectedKeyword]);

  const sorted = useMemo(() => {
    if (!fullScan) return [];
    return sortCompetitors(fullScan.competitors, sortKey);
  }, [fullScan, sortKey]);

  const summary = useMemo(() => buildSummary(fullScan), [fullScan]);

  if (!hasScans) {
    return (
      <div style={{ flex: 1, padding: isMobile ? 14 : 28, background: '#fff' }}>
        <Header />
        <EmptyState />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 14 : 28, background: '#fff' }}>
      <Header keyword={selectedKeyword} client={client} />

      {/* Keyword pills */}
      <KeywordPills scans={lfScans} selected={selectedKeyword} onSelect={setSelectedKeyword} />

      {/* Summary cards */}
      {summary && <SummaryRow summary={summary} />}

      {error && (
        <div style={{
          marginTop: 16, padding: '8px 12px',
          background: 'rgba(232,80,26,0.06)', border: `1px solid ${T.magenta}`, borderRadius: 6,
          fontSize: 12, color: T.magenta, fontFamily: sharedStyles.fontMono,
        }}>Failed to load scan: {error}</div>
      )}

      {/* Competitor table */}
      {!error && (
        <div style={{
          marginTop: 18, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden',
          background: '#fff',
        }}>
          {/* Phones: the fixed 6-column table scrolls sideways instead of crushing. */}
          <div style={{ overflowX: isMobile ? 'auto' : 'visible' }}>
            <div style={{ minWidth: isMobile ? 480 : 'auto' }}>
              <SortHeader sortKey={sortKey} onSort={setSortKey} />
              {loading && <div style={{ padding: 24, textAlign: 'center', color: T.fgMuted, fontSize: 13 }}>Loading competitors…</div>}
              {!loading && sorted.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: T.fgMuted, fontSize: 13 }}>
                  No competitor data in this scan.
                </div>
              )}
              {!loading && sorted.map((c, idx) => (
                <CompetitorRow key={c.placeId} competitor={c} listRank={idx + 1} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Header

function Header({ keyword, client }: { keyword?: string | null; client?: Client }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 22, fontWeight: 700, color: T.fgPrimary, marginBottom: 4 }}>
        Competitor Comparison
        {keyword && <GBIBadge color={T.cyan} style={{ marginLeft: 10, fontSize: 10, verticalAlign: 'middle' }}>Live data</GBIBadge>}
      </div>
      <div style={{ fontSize: 13, color: T.fgSecondary }}>
        {client && <>{client.name} · {client.location}</>}
        {keyword && <> · keyword "{keyword}"</>}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{
      marginTop: 24, padding: 40,
      background: T.bgSubtle, border: `1px dashed ${T.border}`, borderRadius: 12,
      textAlign: 'center', color: T.fgSecondary, fontSize: 13,
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: T.fgPrimary, marginBottom: 6 }}>
        No competitor data yet
      </div>
      <div>
        Open the Heatmap tab and click <strong>Refresh heatmap results</strong> to pull the latest scans. Competitor lists are derived per keyword from those scans.
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Keyword pills (mirrors HeatmapView's design but read-only — refresh lives there)

function KeywordPills({
  scans, selected, onSelect,
}: {
  scans: LfScan[];
  selected: string | null;
  onSelect: (k: string) => void;
}) {
  return (
    <div style={{
      display: 'flex', gap: 8, marginBottom: 14,
      overflowX: 'auto', paddingBottom: 6,
    }}>
      {scans.map(s => {
        const active = s.keyword === selected;
        const solv = s.solv ?? 0;
        const accent = solvAccent(solv);
        return (
          <div
            key={s.keyword}
            onClick={() => onSelect(s.keyword)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 12px',
              borderRadius: 999,
              border: active ? `1.5px solid ${T.cyan}` : `1px solid ${T.border}`,
              background: active ? 'rgba(18,181,196,0.06)' : '#fff',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'all 120ms',
            }}
          >
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: accent, flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: active ? T.fgPrimary : T.fgSecondary, whiteSpace: 'nowrap' }}>
              {s.keyword}
            </span>
            <span style={{ fontSize: 10, color: T.fgMuted, fontFamily: sharedStyles.fontMono }}>
              {solv ? `${solv.toFixed(0)}%` : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Summary cards

interface Summary {
  totalCompetitors: number;
  yourRank: number | null;        // Position in the sorted-by-SoLV list, 1-based; null = not in scan
  yourSolv: number | null;
  yourArp: number | null;
  marketAvgArp: number | null;
  marketMedianSolv: number | null;
  beatingPct: number | null;      // Fraction of competitors strictly below user (by SoLV), 0-100
}

function buildSummary(scan: LfFullScan | null): Summary | null {
  if (!scan) return null;
  const sortedBySolv = sortCompetitors(scan.competitors, 'solv');
  const total = sortedBySolv.length;
  const yourIdx = sortedBySolv.findIndex(c => c.isYou);
  const you = yourIdx >= 0 ? sortedBySolv[yourIdx] : null;

  const arps = sortedBySolv.map(c => c.arp).filter((n): n is number => n != null);
  const solvs = sortedBySolv.map(c => c.solv).filter((n): n is number => n != null);
  const marketAvgArp = arps.length > 0 ? arps.reduce((a, b) => a + b, 0) / arps.length : null;
  const marketMedianSolv = solvs.length > 0 ? median(solvs) : null;

  const beatingPct = you && total > 1
    ? Math.round((sortedBySolv.filter(c => !c.isYou && (c.solv ?? 0) < (you.solv ?? 0)).length / (total - 1)) * 100)
    : null;

  return {
    totalCompetitors: total,
    yourRank: you ? yourIdx + 1 : null,
    yourSolv: you?.solv ?? null,
    yourArp: you?.arp ?? null,
    marketAvgArp,
    marketMedianSolv,
    beatingPct,
  };
}

function SummaryRow({ summary }: { summary: Summary }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 12, marginBottom: 6 }}>
      <SummaryCard
        label="Your Rank"
        value={summary.yourRank ? `#${summary.yourRank}` : '—'}
        sub={summary.yourRank ? `out of ${summary.totalCompetitors}` : 'not in scan'}
        color={summary.yourRank && summary.yourRank <= 3 ? '#188038' : summary.yourRank && summary.yourRank <= 10 ? T.yellow : T.magenta}
      />
      <SummaryCard
        label="Your Avg Rank Position"
        value={summary.yourArp != null ? summary.yourArp.toFixed(2) : '—'}
        sub={summary.marketAvgArp != null ? `Market avg ${summary.marketAvgArp.toFixed(2)}` : 'across grid points'}
        color={T.cyan}
      />
      <SummaryCard
        label="Your SoLV"
        value={summary.yourSolv != null ? `${summary.yourSolv.toFixed(1)}%` : '—'}
        sub={summary.marketMedianSolv != null ? `Market median ${summary.marketMedianSolv.toFixed(1)}%` : ''}
        color="#188038"
      />
      <SummaryCard
        label="Beating"
        value={summary.beatingPct != null ? `${summary.beatingPct}%` : '—'}
        sub={summary.beatingPct != null ? 'of competitors by SoLV' : ''}
        color={T.purple}
      />
    </div>
  );
}

function SummaryCard({ label, value, sub, color }: { label: string; value: string; sub: string; color: string }) {
  return (
    <div style={{ background: '#f8f9fa', border: `1px solid ${T.border}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 10, color: T.fgMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: sharedStyles.fontMono, fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Sortable competitor table

const SORT_LABELS: Record<SortKey, string> = {
  solv: 'SoLV',
  arp: 'Avg Rank',
  atrp: 'Total Rank',
  reviews: 'Reviews',
  rating: 'Rating',
};

function SortHeader({ sortKey, onSort }: { sortKey: SortKey; onSort: (k: SortKey) => void }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '40px 1fr 80px 80px 80px 80px',
      gap: 12, padding: '10px 16px',
      background: T.bgSubtle, borderBottom: `1px solid ${T.border}`,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
      color: T.fgMuted,
    }}>
      <div>#</div>
      <div>Business</div>
      {(['rating', 'arp', 'atrp', 'solv'] as SortKey[]).map(k => (
        <button
          key={k}
          onClick={() => onSort(k)}
          style={{
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            textAlign: 'right',
            fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 10,
            color: sortKey === k ? T.cyan : T.fgMuted,
          }}
        >
          {SORT_LABELS[k]} {sortKey === k && '↓'}
        </button>
      ))}
    </div>
  );
}

function CompetitorRow({ competitor: c, listRank }: { competitor: LfCompetitor; listRank: number }) {
  const positionColor =
    listRank === 1 ? '#188038' :
    listRank <= 3 ? '#34a853' :
    listRank <= 10 ? '#fbbc04' :
    listRank <= 20 ? '#e37400' :
    '#9aa0a6';

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '40px 1fr 80px 80px 80px 80px',
      gap: 12, padding: '12px 16px', alignItems: 'center',
      borderBottom: `1px solid ${T.border}`,
      background: c.isYou ? 'rgba(18,181,196,0.06)' : '#fff',
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        background: positionColor, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: sharedStyles.fontMono, fontSize: 12, fontWeight: 700,
      }}>{listRank}</div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.fgPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {c.name}
          </span>
          {c.isYou && <GBIBadge color={T.cyan} style={{ fontSize: 9, padding: '1px 6px', flexShrink: 0 }}>YOU</GBIBadge>}
        </div>
        <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {c.category ?? '—'}
        </div>
      </div>

      <div style={{ textAlign: 'right', fontSize: 12, color: T.fgPrimary }}>
        {c.rating != null ? <>★ {c.rating.toFixed(1)}<span style={{ color: T.fgMuted, fontSize: 11, marginLeft: 4 }}>({c.reviews ?? 0})</span></> : '—'}
      </div>
      <div style={{ textAlign: 'right', fontFamily: sharedStyles.fontMono, fontSize: 12, color: T.fgPrimary }}>
        {c.arp != null ? c.arp.toFixed(2) : '—'}
      </div>
      <div style={{ textAlign: 'right', fontFamily: sharedStyles.fontMono, fontSize: 12, color: T.fgPrimary }}>
        {c.atrp != null ? c.atrp.toFixed(2) : '—'}
      </div>
      <div style={{ textAlign: 'right' }}>
        <SolvBadge value={c.solv} />
      </div>
    </div>
  );
}

function SolvBadge({ value }: { value: number | null }) {
  if (value == null) return <span style={{ fontSize: 12, color: T.fgMuted }}>—</span>;
  const color = solvAccent(value);
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      background: `${color}18`, color, fontFamily: sharedStyles.fontMono, fontSize: 12, fontWeight: 700,
    }}>{value.toFixed(1)}</span>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers

function sortCompetitors(competitors: LfCompetitor[], key: SortKey): LfCompetitor[] {
  const sign = (key === 'arp' || key === 'atrp') ? 1 : -1; // arp/atrp: lower is better; others: higher is better
  return [...competitors].sort((a, b) => {
    const av = (a[key] ?? (sign === 1 ? Infinity : -Infinity)) as number;
    const bv = (b[key] ?? (sign === 1 ? Infinity : -Infinity)) as number;
    if (av !== bv) return sign * (av - bv);
    return (a.name || '').localeCompare(b.name || '');
  });
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function solvAccent(solv: number): string {
  if (solv >= 60) return '#188038';
  if (solv >= 40) return '#34a853';
  if (solv >= 20) return '#fbbc04';
  if (solv > 0) return '#e37400';
  return '#9aa0a6';
}
